import { motion } from "framer-motion";
import { Shield, Award, Clock, Users, Star, CheckCircle, Zap, Lock, Phone, Heart } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { useLanguage } from "../context/LanguageContext";

const WhyChooseUsSection = () => {
  const { t, language } = useLanguage();
  const whatsappLink = "https://wa.me/905488484828?text=" + encodeURIComponent(t("floatingWhatsapp.defaultMessage"));

  const reasons = [
    {
      icon: Lock,
      titleTr: "Tam Gizlilik",
      titleEn: "Full Privacy",
      descTr: "Müşteri mahremiyeti en önemli önceliğimiz. Tüm hizmetlerimiz tam gizlilik garantisi ile sunulmaktadır.",
      descEn: "Customer privacy is our top priority. All services are provided with complete confidentiality guarantee.",
      color: "#D4AF37"
    },
    {
      icon: Award,
      titleTr: "10+ Yıllık Tecrübe",
      titleEn: "10+ Years Experience",
      descTr: "2014'ten bu yana Kuzey Kıbrıs'ta profesyonel hizmet. Binlerce mutlu misafir.",
      descEn: "Professional service in North Cyprus since 2014. Thousands of happy guests.",
      color: "#00FFFF"
    },
    {
      icon: Heart,
      titleTr: "Seçkin Kadro",
      titleEn: "Select Staff",
      descTr: "Profesyonel, eğitimli ve seçkin konsomatris ve dansöz kadromuz ile kaliteli hizmet.",
      descEn: "Quality service with our professional, trained and select hostess and dancer staff.",
      color: "#FF00FF"
    },
    {
      icon: Zap,
      titleTr: "7/24 Anında Hizmet",
      titleEn: "24/7 Instant Service",
      descTr: "Gece gündüz fark etmez. WhatsApp'tan yazın, 5 dakika içinde dönüş garantisi.",
      descEn: "Day or night doesn't matter. Message on WhatsApp, 5 minute response guarantee.",
      color: "#D4AF37"
    },
    {
      icon: Shield,
      titleTr: "Güvenli & Yasal",
      titleEn: "Safe & Legal",
      descTr: "Tüm hizmetlerimiz yasal çerçevede, güvenli ve profesyonel şekilde sunulmaktadır.",
      descEn: "All services are provided legally, safely and professionally.",
      color: "#00FFFF"
    },
    {
      icon: Users,
      titleTr: "Müşteri Memnuniyeti",
      titleEn: "Customer Satisfaction",
      descTr: "4.8/5 puan ortalaması, 50.000+ misafir. Tekrar tercih oranımız %85'in üzerinde.",
      descEn: "4.8/5 average rating, 50,000+ guests. Our repeat rate is over 85%.",
      color: "#FF00FF"
    }
  ];

  return (
    <section id="why-us" className="py-24 bg-[#050505] relative overflow-hidden" data-testid="why-choose-us-section">
      {/* Background accent */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#D4AF37]/5 rounded-full blur-[150px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-[#FF00FF]/5 rounded-full blur-[150px] pointer-events-none" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-xs font-mono tracking-[0.3em] text-[#D4AF37] uppercase mb-4 block">
            {t("whyChooseUs.subtitle")}
          </span>
          <h2 className="font-['Syne'] text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight uppercase mb-6">
            <span className="text-white">{t("whyChooseUs.title1")}</span>{" "}
            <span className="text-[#D4AF37]">{t("whyChooseUs.title2")}</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg leading-relaxed">
            {language === "tr" 
              ? "First Lady, Kuzey Kıbrıs'ta 10 yılı aşkın tecrübesiyle profesyonel yetişkin eğlence hizmetinin zirvesini sunar."
              : "First Lady offers the pinnacle of professional adult entertainment with over 10 years of experience in North Cyprus."
            }
          </p>
        </motion.div>

        {/* Reasons Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
          {reasons.map((reason, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="bg-white/5 border border-white/10 p-6 hover:border-white/20 transition-all group"
              data-testid={`reason-${index}`}
            >
              <div 
                className="w-14 h-14 flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                style={{ backgroundColor: reason.color }}
              >
                <reason.icon size={28} className="text-black" />
              </div>
              <h3 className="font-['Syne'] text-xl font-bold text-white mb-3 uppercase">
                {language === "tr" ? reason.titleTr : reason.titleEn}
              </h3>
              <p className="text-gray-400 leading-relaxed text-sm">
                {language === "tr" ? reason.descTr : reason.descEn}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Trust Banner */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-gradient-to-r from-[#FF00FF]/20 via-[#FF00FF]/10 to-[#FF00FF]/20 border border-[#FF00FF]/30 p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-['Syne'] text-2xl md:text-3xl font-bold text-white mb-4 uppercase">
                {language === "tr" ? "Hemen Rezervasyon Yapın" : "Book Now"}
              </h3>
              <p className="text-gray-300 mb-6 leading-relaxed">
                {language === "tr" 
                  ? "Konsomatris, dansöz ve VIP transfer hizmetleri için 7/24 WhatsApp hattımızdan anında rezervasyon."
                  : "Instant booking from our 24/7 WhatsApp line for hostess, dancer and VIP transfer services."
                }
              </p>
              
              {/* Urgency badges */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-2 bg-green-500/20 border border-green-500/50 text-green-400 px-3 py-1 text-xs font-bold uppercase">
                  <CheckCircle size={14} />
                  {language === "tr" ? "Anında Onay" : "Instant Confirmation"}
                </span>
                <span className="inline-flex items-center gap-2 bg-[#FF00FF]/20 border border-[#FF00FF]/50 text-[#FF00FF] px-3 py-1 text-xs font-bold uppercase">
                  <Lock size={14} />
                  {language === "tr" ? "Tam Gizlilik" : "Full Privacy"}
                </span>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-[#25D366] text-white px-6 py-4 font-bold tracking-wider uppercase hover:scale-105 transition-all shadow-[0_0_30px_rgba(37,211,102,0.3)]"
                  data-testid="why-us-whatsapp-btn"
                >
                  <FaWhatsapp size={22} />
                  <span>WhatsApp</span>
                </a>
                <a
                  href="tel:+905488484828"
                  className="inline-flex items-center justify-center gap-3 bg-[#FF00FF] text-white px-6 py-4 font-bold tracking-wider uppercase hover:scale-105 transition-all"
                  data-testid="why-us-call-btn"
                >
                  <Phone size={20} />
                  <span>{language === "tr" ? "Hemen Ara" : "Call Now"}</span>
                </a>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-black/30 p-6 text-center border border-white/10">
                <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#D4AF37] mb-1">4.8</div>
                <div className="flex justify-center gap-1 mb-2">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} size={14} className="fill-[#D4AF37] text-[#D4AF37]" />
                  ))}
                </div>
                <p className="text-gray-400 text-xs uppercase tracking-wider">
                  {language === "tr" ? "Puan" : "Rating"}
                </p>
              </div>
              <div className="bg-black/30 p-6 text-center border border-white/10">
                <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#FF00FF] mb-1">50K+</div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mt-2">
                  {language === "tr" ? "Misafir" : "Guests"}
                </p>
              </div>
              <div className="bg-black/30 p-6 text-center border border-white/10">
                <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#00FFFF] mb-1">10+</div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mt-2">
                  {language === "tr" ? "Yıl Tecrübe" : "Years Exp"}
                </p>
              </div>
              <div className="bg-black/30 p-6 text-center border border-white/10">
                <div className="font-['Bebas_Neue'] text-4xl md:text-5xl text-[#D4AF37] mb-1">24/7</div>
                <p className="text-gray-400 text-xs uppercase tracking-wider mt-2">
                  {language === "tr" ? "Hizmet" : "Service"}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyChooseUsSection;
